import { createSelector } from 'reselect';

const getCountrySettings = (state) => state.settings?.country;
const getFeatureFlags = (state) => state.settings?.flags;

const getStandstillConfiguration = createSelector(
  getCountrySettings,
  (countrySettings) => countrySettings?.standstill
);

const getLocale = (state) => state.settings.country.locale;
const getFeatures = (state) => (state.settings.country || {}).features || {};

const getFirstPhone = createSelector(
  getCountrySettings,
  (countrySettings) => (countrySettings.contact_phones || [])[0] || {}
);

const getContactPhone = createSelector(getFirstPhone, (phone) => phone.phone);

const getContactPhoneWithAreaCode = createSelector(
  getFirstPhone,
  (phone) => `${phone.phone_prefix} ${phone.phone}`
);

const getContactPhoneNoSpaces = createSelector(
  getFirstPhone,
  (phone) => `${phone.phone_prefix}${phone.phone_no_spaces}`
);

const getFinanceEmail = createSelector(
  getCountrySettings,
  (countrySettings) => countrySettings.finance_email
);

const getFinancePhone = createSelector(
  getCountrySettings,
  (countrySettings) => countrySettings.finance_phone
);

const getFinancePhoneNoSpaces = createSelector(
  getFinancePhone,
  (financePhone) => (financePhone || '').replace(/ /g, '')
);

const getPalletTypes = createSelector(
  getCountrySettings,
  (countrySettings) => countrySettings.pallet_types || []
);

const getCountry = createSelector(
  getCountrySettings,
  (countrySettings) => countrySettings.country || ''
);

const getOpsMail = createSelector(
  getCountrySettings,
  (countrySettings) => countrySettings.ops_shippers_email || 'ops@ontruck.com'
);

const getZipcodeRegExp = createSelector(
  getCountrySettings,
  (countrySettings) => new RegExp(countrySettings.zipcode_re)
);

const getZipcodeRegExpByCountry = (country) => {
  return createSelector(getCountrySettings, (countrySettings) => {
    const shipperRegExp = countrySettings.zipcode_re;
    const internationalRegExps = countrySettings.international_zipcode_re;

    return !!internationalRegExps[country]
      ? new RegExp(internationalRegExps[country])
      : new RegExp(shipperRegExp);
  });
};

const getLegalLinks = createSelector(
  getCountrySettings,
  (countrySettings) => countrySettings.links || {}
);

const getUnits = createSelector(
  getCountrySettings,
  (countrySettings) => countrySettings.units
);

const getCurrency = createSelector(getCountrySettings, (countrySettings) => ({
  symbol: countrySettings.currency_symbol,
  currency: countrySettings.currency,
}));

const getLoadsInStop = (stop) => {
  const { load_pickup: loadPickup, load_dropoff: loadDropoff } = stop;

  const loadsInStop = [...(loadPickup || []), ...(loadDropoff || [])];

  const { numberOfItems, weight, count } = loadsInStop.reduce(
    (values, load) => ({
      numberOfItems: values.numberOfItems + load.eu_pallets,
      weight: values.weight + load.total_weight,
      count: values.count + load.number_of_items,
    }),
    {
      numberOfItems: 0,
      weight: 0,
      count: 0,
    }
  );

  return { numberOfItems, weight, count };
};

const getStandstillConfigByStopLoad = createSelector(
  getStandstillConfiguration,
  (standstillConfigs) => (stop) => {
    const { numberOfItems, weight } = getLoadsInStop(stop);
    return standstillConfigs?.reduce((current, sc) => {
      if (
        (sc.max_pallets || sc.max_eu_pallets) <= numberOfItems ||
        sc.max_weight <= weight
      ) {
        return sc;
      }
      return current;
    }, standstillConfigs?.[0]);
  }
);

const getInviteUserModalOpened = createSelector(
  (state) => state.settings.inviteUserModalOpened,
  (inviteUserModalOpened) => inviteUserModalOpened
);

export default {
  getCountrySettings,
  getFeatureFlags,
  getLocale,
  getFeatures,
  getContactPhone,
  getContactPhoneNoSpaces,
  getFinanceEmail,
  getFinancePhone,
  getFinancePhoneNoSpaces,
  getPalletTypes,
  getCountry,
  getOpsMail,
  getZipcodeRegExp,
  getLegalLinks,
  getUnits,
  getCurrency,
  getStandstillConfigByStopLoad,
  getLoadsInStop,
  getInviteUserModalOpened,
  getZipcodeRegExpByCountry,
  getContactPhoneWithAreaCode,
};
